import React, { useMemo } from 'react';
import { useMst } from '../models/WithWineState';
import { PriceDisplayProps } from '../types/PriceDisplayProps';
import ProductPrice from './ProductPrice';
import { useObserver, useLocalStore } from 'mobx-react-lite';
import { formatCurrency } from '../utils/formatCurrencyWithoutDecimals';

const displayOptionsList = [
  'price',
  'bottlePrice',
  'bottlePriceHigh',
  'fullPrice',
  'singlePrice',
  'halfDozenPrice',
  'dozenPrice',
  'inAny6Price',
  'inAny12Price',
  'minimumUnitPrice',
  'minimumUnitPricePublic',
  'calculatedUserPrice',
  'override',
];

const PriceDisplay: React.FC<PriceDisplayProps> = (props) => {
  const stateTree = useMst();
  const { data, priceFormat } = props;
  const {
    display,
    decimals: decimalFormat,
    prefix,
    suffix,
    override,
  } = priceFormat ?? {};

  const state = useLocalStore(() => ({
    get isAuth() {
      return stateTree.session?.authenticated ?? false;
    },
  }));

  const displayOptions = useMemo(() => display?.split('|'), [display]);

  const decimals = useMemo(() => {
    let dec: number | null = 2;
    if (decimalFormat) {
      if (typeof decimalFormat === 'string') {
        if (decimalFormat.toLowerCase() === 'currency') {
          dec = null;
        } else {
          dec = parseInt(decimalFormat);
          if (isNaN(dec)) {
            dec = 2;
          }
        }
      } else if (typeof decimalFormat === 'number') {
        dec = decimalFormat;
      }
    }
    return dec;
  }, [decimalFormat]);

  const productPriceData: { [key: string]: number | null } = useMemo(
    () =>
      displayOptions?.reduce((acc, cur) => {
        if (displayOptionsList.includes(cur)) {
          return {
            ...acc,
            [cur]: (data as any)[cur],
          };
        }
        return acc;
      }, {}) ?? {},
    [displayOptions, data],
  );

  const [
    showPrice,
    price,
    pricePrefix,
    priceSuffix,
    showProductPriceComponent,
  ] = useMemo(() => {
    // TODO: these references need to be fixed (@typescript-eslint/no-shadow),
    // but I don't want to change them without a test to cover the current
    // functionality!
    /* eslint-disable */
    let showPrice: boolean | undefined;
    let price: string | undefined;
    let pricePrefix = prefix;
    let priceSuffix = suffix;
    let showProductPriceComponent: boolean | undefined;
    /* eslint-enable */

    if (typeof displayOptions !== 'undefined') {
      if (!displayOptions || displayOptions.length === 0) {
        showPrice = false;
      } else if (displayOptions) {
        const prefixOptions = pricePrefix?.split('|') ?? [];
        const suffixOptions = priceSuffix?.split('|') ?? [];
        let displayVal: number | null = null;

        for (let i = 0; i < displayOptions.length; i++) {
          const displayOption = displayOptions[i];

          if (displayOption === 'userPrice') {
            if (state.isAuth) {
              showProductPriceComponent = true;
            }
          } else if (displayOption === 'productPrice') {
            showProductPriceComponent = true;
          } else if (displayOption === 'override') {
            displayVal =
              typeof override === 'string'
                ? parseFloat(override.trim()) ?? null
                : override ?? null;
          } else {
            displayVal = productPriceData[displayOption];
          }

          if (displayVal && typeof displayVal === 'number') {
            price = formatCurrency(
              displayVal,
              data?.brand?.paymentCurrency ?? '',
              { fractionDigits: decimals },
            );
            showPrice = true;
          }

          if (
            (displayVal && typeof displayVal === 'number') ||
            showProductPriceComponent
          ) {
            if (i < prefixOptions.length) {
              pricePrefix = prefixOptions[i];
            } else if (prefixOptions.length > 0) {
              pricePrefix = prefixOptions[0];
            }

            if (i < suffixOptions.length) {
              priceSuffix = suffixOptions[i];
            } else if (suffixOptions.length > 0) {
              priceSuffix = suffixOptions[0];
            }

            break;
          }
        }
      }
    }

    if (
      pricePrefix &&
      pricePrefix.length > 0 &&
      pricePrefix.charAt(pricePrefix.length - 1) !== ' '
    ) {
      pricePrefix = pricePrefix + ' ';
    }

    if (
      priceSuffix &&
      priceSuffix.length > 0 &&
      priceSuffix.charAt(0) !== ' '
    ) {
      priceSuffix = ' ' + priceSuffix;
    }

    return [
      showPrice ?? typeof data.price === 'number',
      price ??
        formatCurrency(data.price, data?.brand?.paymentCurrency ?? '', {
          fractionDigits: decimals,
        }),
      pricePrefix ?? 'From ',
      priceSuffix ??
        (data.variationTypeDescription
          ? ' per ' + data.variationTypeDescription.toLowerCase()
          : data.isNonWineProduct
          ? ' each'
          : ' per bottle'),
      showProductPriceComponent ?? false,
    ];
  }, [
    prefix,
    suffix,
    state.isAuth,
    decimals,
    displayOptions,
    productPriceData,
    data.price,
    data.variationTypeDescription,
    data.isNonWineProduct,
    override,
    data?.brand?.paymentCurrency,
  ]);

  return useObserver(() => {
    if (data.isSaleable && (showPrice || showProductPriceComponent)) {
      return (
        <div className="price-info">
          {!props.hidePricePrefix && (
            <span className="price-info-prefix">{pricePrefix}</span>
          )}

          {showProductPriceComponent ? (
            <ProductPrice
              product_id={props.data.id.toString()}
              formatted={true}
              formatoptions={{
                minfractiondigits: decimals ?? undefined,
                maxfractiondigits: decimals ?? undefined,
              }}
            />
          ) : (
            <span className="price-info-content">{price}</span>
          )}

          {!props.hidePriceSuffix && (
            <span className="price-info-suffix">{priceSuffix}</span>
          )}
        </div>
      );
    } else {
      return <></>;
    }
  });
};

export default PriceDisplay;
